import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Footer from './Footer';
import type { Lot } from '../contexts/LotContext';

function LotsPage() {
  const [lots, setLots] = useState<Lot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAvailableLots();
  }, []);

  const fetchAvailableLots = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('lots')
        .select('*')
        .eq('status', 'active')
        .neq('id', 'd0cc121f-f615-4116-9d1f-46dc53c93c07')
        .order('display_name');

      if (fetchError) {
        throw fetchError;
      }

      setLots(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load parking lots';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getLotColor = (index: number) => {
    const colors = [
      { bg: 'bg-blue-50', hover: 'hover:bg-blue-100', text: 'text-blue-600' },
      { bg: 'bg-green-50', hover: 'hover:bg-green-100', text: 'text-green-600' },
      { bg: 'bg-purple-50', hover: 'hover:bg-purple-100', text: 'text-purple-600' },
      { bg: 'bg-orange-50', hover: 'hover:bg-orange-100', text: 'text-orange-600' },
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex shadow-md rounded overflow-hidden h-6">
              <div className="w-4 h-full bg-green-600"></div>
              <div className="w-4 h-full bg-white"></div>
              <div className="w-4 h-full bg-red-600"></div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Parking Little Italy</h1>
          </div>
          <p className="mt-2 text-gray-600">Select your parking location</p>
        </div>

        <div className="grid gap-4">
          {isLoading && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading parking lots...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center p-4 bg-red-50 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {!isLoading && !error && lots.length === 0 && (
            <div className="text-center p-4 text-gray-600">
              No parking lots available at the moment.
            </div>
          )}

          {!isLoading && !error && lots.map((lot, index) => {
            const colors = getLotColor(index);
            return (
              <Link
                key={lot.id}
                to={`/lots/${lot.slug}`}
                className={`flex items-center justify-between p-4 ${colors.bg} rounded-lg ${colors.hover} transition duration-200`}
              >
                <div className="flex items-center gap-3">
                  <MapPin className={colors.text} size={24} />
                  <div className="text-left">
                    <h2 className="font-semibold text-gray-900">{lot.display_name}</h2>
                    <p className="text-sm text-gray-600">
                      {lot.description || lot.address || 'Convenient parking location'}
                    </p>
                  </div>
                </div>
                <div className={colors.text}>→</div>
              </Link>
            );
          })}
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default LotsPage;

